/** IANA timezone helpers for user profile hover cards and settings. */

const FALLBACK_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

export function getSupportedTimezones(): string[] {
  try {
    const intl = Intl as typeof Intl & { supportedValuesOf?(key: string): string[] };
    if (typeof intl.supportedValuesOf === 'function') {
      const list = intl.supportedValuesOf('timeZone');
      if (Array.isArray(list) && list.length > 0) return list;
    }
  } catch {
    /* ignore */
  }
  return FALLBACK_TIMEZONES;
}

export function isValidIanaTimezone(tz: string): boolean {
  const trimmed = tz.trim();
  if (!trimmed) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    return true;
  } catch {
    return false;
  }
}

export function getViewerTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** UTC offset in minutes for a timezone at a given instant (positive = east of UTC). */
export function getUtcOffsetMinutes(timeZone: string, date: Date = new Date()): number {
  if (!isValidIanaTimezone(timeZone)) return 0;
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = dtf.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'));
  return Math.round((asUtc - date.getTime()) / 60000);
}

export function formatUserLocalTime(timeZone: string, date: Date = new Date()): string {
  if (!isValidIanaTimezone(timeZone)) return '';
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Relative offset from viewer to user timezone.
 * Positive diff means user is ahead of viewer.
 */
export function formatTimezoneOffsetDiff(
  viewerTz: string,
  userTz: string,
  date: Date = new Date(),
): string {
  if (!isValidIanaTimezone(userTz)) return '';
  if (!isValidIanaTimezone(viewerTz)) viewerTz = 'UTC';

  const viewerOffset = getUtcOffsetMinutes(viewerTz, date);
  const userOffset = getUtcOffsetMinutes(userTz, date);
  const diffHours = Math.round((userOffset - viewerOffset) / 60);

  if (diffHours === 0) return 'Same time as you';
  const abs = Math.abs(diffHours);
  const unit = abs === 1 ? '1h' : `${abs}h`;
  return diffHours > 0 ? `${unit} ahead of you` : `${unit} behind you`;
}

export function formatUserLocalTimeWithOffset(
  userTz: string,
  viewerTz: string = getViewerTimezone(),
  date: Date = new Date(),
): string {
  const time = formatUserLocalTime(userTz, date);
  if (!time) return '';
  const offset = formatTimezoneOffsetDiff(viewerTz, userTz, date);
  return offset ? `${time} · ${offset}` : time;
}
